import { TFunction } from 'hooks/useI18n';
import { ListItem } from 'interfaces/ui/ui-list-item';
import isEmpty from 'lodash/isEmpty';
import { OfferLanguage } from 'model';
import { OffersListFilter } from 'model/model-offers-list-filter';
import { getTranslatedText } from 'services/multilingual.service';
import { getCityStringFromUIPlacesAutocompleteItem } from 'services/ui/offer.service';
import { OFFER_FILTER_QUERY_PARAMS } from 'utils/job-offers-filter.constants';
import { REMOTE_OPTIONS } from './filters/Filters.constants';
import { DEFAULT_FILTER } from './JobOffersListComponent.constants';

type GetTextForOffersListFilterArgs = {
  filter: OffersListFilter;
  t: TFunction;
  allMinLanguages: ListItem[];
  languages: OfferLanguage[];
  techs: ListItem[];
  locale?: string;
};

export function getTextForOffersListFilter({
  filter,
  t,
  languages,
  allMinLanguages,
  techs,
  locale
}: GetTextForOffersListFilterArgs): string[] {
  const filtersText = [];

  if (filter.onlyActive) {
    filtersText.push(t('filters_onlyActive'));
  }

  if (filter.remoteOptions && filter.remoteOptions.length > 0) {
    filtersText.push(
      t('filters_remoteOptionsRemote', {
        remotePercentage: REMOTE_OPTIONS.filter((opt) =>
          filter.remoteOptions?.includes(opt.value)
        )
          .map((opt) => t(`remote_options_items.${opt.label}`))
          .filter(Boolean)
          .join(', ')
      })
    );
  }

  if (filter.techs && filter.techs.length > 0) {
    filtersText.push(
      t('filters_techs', {
        techs: techs
          .filter((opt) => filter.techs?.includes(+opt.value))
          .map((opt) => opt.label)
          .filter(Boolean)
          .join(', ')
      })
    );
  }

  if (filter.locations && filter.locations.length > 0) {
    filtersText.push(
      t('filters_city', {
        city: getCityStringFromUIPlacesAutocompleteItem(
          filter.locations,
          locale
        )
      })
    );
  }

  if (filter.text) {
    filtersText.push(t('filters_text', { text: filter.text }));
  }

  if (filter.currency && filter.minSalary) {
    filtersText.push(
      t('filters_minSalary', {
        minSalary: filter.minSalary,
        currency: filter.currency
      })
    );
  }

  if (filter.minLanguages) {
    filtersText.push(
      t('filters_minLanguages', {
        minLanguages: filter.minLanguages
          .map(
            (minLang) =>
              `${
                allMinLanguages.find(
                  (lang) => lang.value === minLang.language.value
                )?.label
              } (${t('language-selector.level.options.' + minLang.level.label, {
                ns: 'common'
              })})`
          )
          .join(', ')
      })
    );
  }

  if (filter.offerLanguage) {
    filtersText.push(
      t('filters_language', {
        language: getValueForCurrentFilterLanguages(filter, languages, locale)
      })
    );
  }

  return filtersText;
}

export function hasFiltersInUrlParams(params: URLSearchParams): boolean {
  return OFFER_FILTER_QUERY_PARAMS.some((param) => !isEmpty(params.get(param)));
}

export function checkIsDefaultFilter(filter: OffersListFilter): boolean {
  const hasCityFilter = !isEmpty(filter?.locations);
  const hasRemoteOptionsFilter = !isEmpty(filter?.remoteOptions);
  const hasTechsFilter = !isEmpty(filter?.techs);
  const hasTechRolesFilter = !isEmpty(filter?.techRoles);
  const hasMinLanguagesFilter = !isEmpty(filter?.minLanguages);

  return (
    filter?.currency === DEFAULT_FILTER.currency &&
    filter?.onlyActive === DEFAULT_FILTER.onlyActive &&
    !hasCityFilter &&
    !hasRemoteOptionsFilter &&
    !hasTechsFilter &&
    !hasTechRolesFilter &&
    !hasMinLanguagesFilter &&
    !filter?.minSalary &&
    !filter?.offerLanguage &&
    !filter?.text
  );
}

function getValueForCurrentFilterLanguages(
  filter: OffersListFilter,
  languages: OfferLanguage[],
  locale?: string
): string {
  if (filter.offerLanguage) {
    const selectedLanguage = languages.find(
      (language) => language.code === filter.offerLanguage
    );
    return getTranslatedText(selectedLanguage?.name, locale);
  } else {
    return '';
  }
}
